import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { VideoCamera, Warning, FloppyDisk, Eye, Buildings, Cube } from "@phosphor-icons/react";

const HEALTH = {
  UP: { c: "#30D158", t: "UP" },
  DOWN: { c: "#FF3B30", t: "DOWN" },
  AUTH_FAILED: { c: "#FF9F0A", t: "AUTH FAILED" },
  NO_FRAME: { c: "#71717A", t: "NO FRAME" },
};

const ZONE_COLOR = (type) => ({
  slab_side: "#30D5C8", aisle: "#0A84FF", display: "#5E5CE6", showroom: "#BF5AF2",
  entry_line: "#30D158", entry_path: "#30D158", door: "#30D158", interior_door: "#30D158",
  counter_customer: "#64D2FF", cashier_zone: "#FF375F", drawer: "#FF3B30", pocket_risk: "#FF9F0A",
  blind_spot: "#8E8E93", excluded: "#3A3A3C",
}[type] || "#A1A1AA");

function Badge({ health }) {
  const h = HEALTH[health] || HEALTH.NO_FRAME;
  return (
    <span data-testid="av-health" className="font-mono-data text-[10px] px-2 py-0.5 border uppercase tracking-wider"
      style={{ color: h.c, borderColor: h.c + "66", background: h.c + "1a" }}>{h.t}</span>
  );
}

function CameraCard({ cam, onSaved, onPreview }) {
  const [rtsp, setRtsp] = useState(cam.rtsp_url || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/avstone/cameras/${cam.id}`, { rtsp_url: rtsp });
      toast.success(`${cam.id} saved`);
      onSaved();
    } catch (e) { toast.error("Save failed"); }
    setBusy(false);
  };
  const toggle = async () => {
    try { await api.put(`/avstone/cameras/${cam.id}`, { active: !cam.active }); onSaved(); } catch (e) {}
  };
  const drift = cam.preset_drift || cam.calibration_drift;
  return (
    <div data-testid={`avstone-camera-${cam.id}`} className="border border-white/10 bg-[#121214] p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-display font-bold text-sm flex items-center gap-2">
            <VideoCamera size={15} weight="bold" className="text-[#0A84FF]" /> {cam.name}
          </div>
          <div className="font-mono-data text-[10px] text-[#71717A] mt-0.5">
            {cam.id} · {cam.model} · {cam.role}{cam.bay_id ? ` · Bay ${cam.bay_id}` : ""}
            {cam.ptz ? " · PTZ" : " · FIXED"}
          </div>
        </div>
        <Badge health={cam.health} />
      </div>

      {cam.zone_groups?.length > 0 && (
        <div className="font-mono-data text-[10px] text-[#64D2FF] mb-2">Zone groups: {cam.zone_groups.join(" + ")}</div>
      )}
      {cam.home_preset_required && (
        <div className="font-mono-data text-[10px] text-[#FF9F0A] mb-2 flex items-center gap-1">
          <Warning size={11} weight="bold" /> Lock to HOME PRESET before calibration (never analyze a moving PTZ view)
        </div>
      )}
      {drift && (
        <div data-testid={`av-drift-${cam.id}`} className="font-mono-data text-[10px] text-[#FF3B30] mb-2 flex items-center gap-1">
          <Warning size={11} weight="bold" /> {cam.preset_drift ? "HOME-PRESET DRIFT — view moved from reference" : "CALIBRATION DRIFT — slab count will be skipped"}
        </div>
      )}

      <label className="font-mono-data text-[10px] text-[#71717A] uppercase tracking-wider">RTSP URL (password hidden)</label>
      <div className="flex gap-2 mt-1">
        <input data-testid={`avstone-rtsp-${cam.id}`} value={rtsp} onChange={(e) => setRtsp(e.target.value)}
          placeholder="rtsp://user:pass@192.168.x.x:554/stream1"
          className="flex-1 bg-[#0A0A0C] border border-white/15 text-white text-xs font-mono-data px-2 py-1.5 focus:border-[#0A84FF] focus:outline-none" />
        <button data-testid={`avstone-save-${cam.id}`} onClick={save} disabled={busy}
          className="px-3 bg-[#0A84FF] hover:bg-[#3395FF] disabled:opacity-40 text-white text-xs flex items-center gap-1">
          <FloppyDisk size={13} weight="bold" /> Save
        </button>
      </div>
      <div className="flex items-center justify-between mt-3">
        <button data-testid={`avstone-preview-${cam.id}`} onClick={() => onPreview(cam)}
          className="text-xs text-[#30D5C8] hover:text-white flex items-center gap-1 font-mono-data">
          <Eye size={13} weight="bold" /> Preview zones
        </button>
        <button onClick={toggle} className={`text-[10px] font-mono-data px-2 py-0.5 border ${cam.active ? "text-[#30D158] border-[#30D158]/40" : "text-[#71717A] border-white/15"}`}>
          {cam.active ? "ACTIVE" : "DISABLED"}
        </button>
      </div>
    </div>
  );
}

function ZoneOverlay({ cam, zones, onClose }) {
  const [still, setStill] = useState(null);
  useEffect(() => {
    api.get(`/avstone/cameras/${cam.id}/still`).then((r) => setStill(r.data)).catch(() => {});
  }, [cam.id]);
  const camZones = zones.filter((z) => (z.camera_ids || []).includes(cam.id));
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="avstone-zone-overlay" onClick={onClose}>
      <div className="bg-[#121214] border border-white/15 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
          <div className="font-display font-bold text-sm">{cam.name} — zone overlay <span className="text-[#71717A] font-mono-data text-[10px]">({camZones.length} zones{still && !still.live ? " · placeholder still" : ""})</span></div>
          <button onClick={onClose} className="text-[#71717A] hover:text-white text-sm">✕</button>
        </div>
        <div className="relative">
          {still?.image && <img src={still.image} alt={cam.id} className="w-full block" style={{ maxHeight: "60vh", objectFit: "contain" }} />}
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            {camZones.map((z) => {
              const pts = (z.polygon || []).map((p) => `${p[0] * 100},${p[1] * 100}`).join(" ");
              const col = ZONE_COLOR(z.type);
              return (
                <g key={z.zone_id}>
                  <polygon points={pts} fill={col + "26"} stroke={col} strokeWidth="0.4"
                    strokeDasharray={z.type === "blind_spot" ? "2 1" : z.active ? "0" : "1 1"} />
                  {z.polygon?.[0] && (
                    <text x={z.polygon[0][0] * 100 + 0.8} y={z.polygon[0][1] * 100 + 2.6} fill={col} fontSize="2" fontFamily="monospace">{z.zone_id}</text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
        <div className="px-4 py-2 font-mono-data text-[10px] text-[#71717A]">Polygons are editable (normalised). Reposition in a later milestone's zone editor.</div>
      </div>
    </div>
  );
}

function ExclusionPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/avstone/exclusion").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const Stat = ({ id, label, value, color }) => (
    <div className="border border-white/10 px-3 py-2" data-testid={id}>
      <div className="font-mono-data text-[10px] text-[#71717A] uppercase tracking-wider">{label}</div>
      <div className="font-display font-bold text-lg" style={{ color }}>{value}</div>
    </div>
  );
  return (
    <div data-testid="avstone-exclusion-panel" className="border border-white/10 bg-[#121214] p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-display font-bold text-sm">Employee exclusion · {d.date} <span className="font-mono-data text-[10px] text-[#71717A]">(Milestone 2 — staff never counted as customers, no staff tracking)</span></div>
        <a data-testid="avstone-agent-download" href={`${API}/agent/launcher/avstone`} className="text-xs font-mono-data text-[#0A84FF] hover:text-white">Download A V Stone edge launcher (.bat)</a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat id="av-ex-customers" label="Customer tracks" value={d.customer_tracks} color="#30D158" />
        <Stat id="av-ex-staff" label="Staff excluded" value={d.staff_excluded} color="#FF9F0A" />
        <Stat id="av-ex-enrolled" label="Enrolled staff" value={d.enrolled_staff} color="#FFFFFF" />
        <Stat id="av-ex-faceid" label="Staff w/ Face ID" value={`${d.staff_with_face_id}/${d.enrolled_staff}`} color="#64D2FF" />
        <Stat id="av-ex-agent" label="Edge agent" value={d.agent_last_seen ? new Date(d.agent_last_seen).toLocaleTimeString() : "offline"} color="#A1A1AA" />
      </div>
      {d.enrolled_staff === 0 && (
        <div data-testid="av-ex-enroll-hint" className="font-mono-data text-[11px] text-[#FF9F0A]">
          No A V Stone staff enrolled yet — add each employee with a clear frontal photo on the People page (while viewing A V Stone) so they are excluded.
        </div>
      )}
    </div>
  );
}

export default function AvStoneCameras() {
  const [cams, setCams] = useState([]);
  const [zones, setZones] = useState([]);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/avstone/cameras").then((r) => setCams(r.data || [])).catch((e) => setErr(e?.response?.data?.detail || "Access denied"));
    api.get("/avstone/zones").then((r) => setZones(r.data?.zones || [])).catch(() => {});
  };
  useEffect(load, []);

  const office = cams.filter((c) => ["showroom", "entrance_cashier", "entrance", "cashier", "interior_door"].includes(c.role));
  const warehouse = cams.filter((c) => c.role === "warehouse");
  const loading = cams.filter((c) => c.role === "dock" || c.role === "gate");

  return (
    <div data-testid="avstone-page" className="p-6 space-y-6">
      <div>
        <h1 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
          <Buildings size={22} weight="bold" className="text-[#0A84FF]" /> A V Stone Corp — Cameras & Zones
        </h1>
        <p className="font-mono-data text-xs text-[#71717A] mt-1">
          8-camera registry · isolated to A V Stone · zones editable · RTSP passwords hidden.
        </p>
      </div>

      {!err && <ExclusionPanel />}

      {err && <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-sm px-3 py-2 font-mono-data" data-testid="avstone-error">{err}</div>}

      {office.length > 0 && (
        <div>
          <div className="font-mono-data text-[11px] text-[#A1A1AA] uppercase tracking-widest mb-2">Office side · Tapo C520WS (PTZ → home preset)</div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {office.map((c) => <CameraCard key={c.id} cam={c} onSaved={load} onPreview={setPreview} />)}
          </div>
        </div>
      )}

      {warehouse.length > 0 && (
        <div>
          <div className="font-mono-data text-[11px] text-[#A1A1AA] uppercase tracking-widest mb-2 flex items-center gap-1">
            <Cube size={13} weight="bold" /> Warehouse · Tapo C325WB (fixed — do not move after calibration)
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {warehouse.map((c) => <CameraCard key={c.id} cam={c} onSaved={load} onPreview={setPreview} />)}
          </div>
        </div>
      )}

      {loading.length > 0 && (
        <div data-testid="avstone-loading-cams">
          <div className="font-mono-data text-[11px] text-[#A1A1AA] uppercase tracking-widest mb-2">
            Truck loading spots & gate · paste each new camera's RTSP URL, then restart the A V Stone launcher
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {loading.map((c) => <CameraCard key={c.id} cam={c} onSaved={load} onPreview={setPreview} />)}
          </div>
        </div>
      )}

      {cams.length > 0 && (
        <div className="font-mono-data text-xs text-[#52525B]" data-testid="avstone-count">Total A V Stone cameras: {cams.length}</div>
      )}

      {preview && <ZoneOverlay cam={preview} zones={zones} onClose={() => setPreview(null)} />}
    </div>
  );
}
